import { Me } from '../types';
import { CONTAINER_SUBS, EXPENSE_KINDS } from '../modules';

// ── The operator's own default page (Calem, report bbd25b1a) ──────────────────────────────────────
// "Allow users to select their default login page in the menu. Also when the user does a browser refresh, load the
// same page they were on prior to clicking refresh."
// The refresh half needed nothing: every page already keeps its place in the address — /m/<key>, the Dashboard's
// ?tab=, the Peak / Panda Marketing / SLF Property / Tax sub-tabs, /focus?item= — and the app is served from any of
// them, so the browser's own reload lands back on the page it was on.
// This is the other half: the page a SIGN-IN opens on, until now always the Portal Dashboard. It is chosen in the
// ≡ logo menu's "Default page" select (components/Portal) and kept per user on the server (routes/homePage.ts, on
// /api/me as `homePage`) in the shapes below.
// It is applied at sign-in ONLY, and once per browser session (components/App.tsx) — never on any other address and
// never twice — so a refresh, and any later visit to the Dashboard, leave the operator exactly where they are.
export const homeTabValue = (tabKey: string) => `tab:${tabKey}`;        // a tab of the Dashboard's own strip → /?tab=<key>
export const homeModuleValue = (moduleKey: string) => `m:${moduleKey}`;  // a page on the ≡ menu → /m/<key>
export const homeDashValue = (dashId: string) => `dash:${dashId}`;      // one of the user's saved My Dashboard(s) → /focus?d=<id>

// The ≡ menu's two container pages are no permission of their own: any folded-in sub-module opens them
// (modules.ts CONTAINER_SUBS, App.tsx RequireAny).
const PEAK_TABS = new Set(['quotation', 'invoice', 'receipt', 'outbox']);   // the Peak module's four Dashboard tabs
const NO_MODULE = new Set(['version', 'bugify']);   // every staff login's, behind no permission at all (App.tsx)

// Whether this login may open that page — the same reading of /api/me the menus take, so a page whose permission has
// since been taken away is never landed on. me.modules is the server's own answer and already holds every module an
// admin has, minus any reserved to one person, so it is read straight. Each page's data stays gated by its own route.
// Exported since report c2846304: an app-wide pop-up is only ever sent to a page this login can actually open
// (services/popupHome.ts), and is asked where the user is when it cannot.
export const mayOpen = (key: string, me: Me): boolean =>
  // The two Approvals buttons of the top menu go by what this login approves, not by a module (report e58ddb81).
  key === 'approvals_doc' ? !!me.approves?.tracking || !!me.approves?.filing
  : key === 'approvals_jobs' ? !!me.approves?.job_complete
  : key === 'email_check' ? !!me.approves?.email_check
  : NO_MODULE.has(key) ? true
    : CONTAINER_SUBS[key] ? me.role === 'admin' || CONTAINER_SUBS[key].some(k => me.modules.includes(k))
      : me.modules.includes(PEAK_TABS.has(key) ? 'peak' : key);

// The Accounting button's tab, `expense`, stands for its five account classes and, since report 790f8217, for the three
// documents under Revenue, so any of those puts it on the strip (Portal: canSeeTab) and the Default page select offers
// it. Being offered, it has to land too. Only the tab: /m/expense, a page of its own, still takes its own permission.
const mayOpenTab = (key: string, me: Me): boolean => (key === 'expense'
  ? me.role === 'admin' || [...EXPENSE_KINDS, 'peak'].some(k => me.modules.includes(k))
  : mayOpen(key, me));

/** Where a saved default page sends the browser — '' for the Dashboard itself, which is also what an unreadable
 *  value, and a page this login may no longer open, fall back to. */
export function homePagePath(page: string | undefined, me: Me): string {
  const m = /^(tab|m|dash):(.+)$/.exec(String(page || ''));
  if (!m) return '';
  const [, kind, key] = m;
  // A saved dashboard deleted since says so on the page itself (components/FocusDashboard: ql_load_failed).
  if (kind === 'dash') return `/focus?d=${encodeURIComponent(key)}`;
  if (!(kind === 'tab' ? mayOpenTab(key, me) : mayOpen(key, me))) return '';
  return kind === 'tab' ? `/?tab=${encodeURIComponent(key)}` : `/m/${encodeURIComponent(key)}`;
}

// Once per browser session. The turn is taken on the first authenticated page load of a session, whatever address
// that load is on — so a session that opened on a page of its own, or that signed in (LoginPage applies the choice
// itself), has had its landing, and a later bare '/' leaves the operator there. sessionStorage is per browser tab and
// survives a refresh, which is exactly the span wanted; where it cannot be written (private mode) the answer is no,
// so a refresh is never hijacked.
const TURN_KEY = 'mf_home_landed';
export function takeHomeTurn(userId: string): boolean {
  try {
    const k = `${TURN_KEY}:${userId}`;
    if (sessionStorage.getItem(k)) return false;
    sessionStorage.setItem(k, '1');
    return true;
  } catch { return false; }
}
